import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import type { InferRequestType, InferResponseType } from 'hono/client'
import { api, unwrap } from '~/lib/api'

// Keys start with 'tactics', the prefix the server invalidates after every change.
export const tacticsKeys = {
  all: ['tactics'] as const,
  list: (teamId: string) => ['tactics', 'list', teamId] as const,
  board: (id: string) => ['tactics', 'board', id] as const,
}

const boards = api['tactics-boards']
export type BoardList = InferResponseType<(typeof boards)['$get'], 200>
export type BoardDetail = InferResponseType<(typeof boards)[':id']['$get'], 200>
export type TacticsBoard = BoardDetail['board']
export type BoardPatch = InferRequestType<(typeof boards)[':id']['$patch']>['json']

export const boardsQuery = (teamId: string) =>
  queryOptions({
    queryKey: tacticsKeys.list(teamId),
    queryFn: (): Promise<BoardList> => unwrap(boards.$get({ query: teamId ? { teamId } : {} })),
  })

export const boardQuery = (id: string) =>
  queryOptions({
    queryKey: tacticsKeys.board(id),
    queryFn: (): Promise<BoardDetail> => unwrap(boards[':id'].$get({ param: { id } })),
  })

export const saveBoard = (id: string, json: BoardPatch) => unwrap(boards[':id'].$patch({ param: { id }, json }))

export function useBoardMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: tacticsKeys.all })
  return {
    create: useMutation({
      mutationFn: (json: InferRequestType<(typeof boards)['$post']>['json']) => unwrap(boards.$post({ json })),
      onSuccess: refresh,
    }),
    duplicate: useMutation({
      mutationFn: (id: string) => unwrap(boards[':id'].duplicate.$post({ param: { id }, json: {} })),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => unwrap(boards[':id'].$delete({ param: { id } })),
      onSuccess: (_, id) => {
        qc.removeQueries({ queryKey: tacticsKeys.board(id) })
        return refresh()
      },
    }),
    restore: useMutation({ mutationFn: (id: string) => unwrap(boards[':id'].restore.$post({ param: { id } })), onSuccess: refresh }),
  }
}
